import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import SpineWrapper, { ICancelablePromise } from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import AssetsLoadManager from '@Core/Loader/AssetsLoadManager';
import { ESymbolAnimations } from '@Core/Reels/SymbolAnimations';
import { ISymbolsHighlightPresenter, WinlineCycleStep } from '@Core/SymbolsHighlighter/SymbolsHighlighter.types';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { GeneratedWinlines } from '../GeneratedWinlines';
import { ESymbolsHighlighterPhases } from '../SymbolsHighlighterPhases';
import { TrackEntry } from '@esotericsoftware/spine-pixi-v8';

export class Winlines extends GameComponent implements ISymbolsHighlightPresenter {
    public scene: GameScene;
    private winlinesSpine: SpineWrapper;
    private winlineDurations: number[] = [];
    private generatedWinlines: GeneratedWinlines;
    private static readonly DEFAULT_CYCLE_TIME = 1000;

    constructor(scene: GameScene) {
        super(scene);
        this.scene = scene;
        Logger.log(LoggerLevel.Constructor, { class: Winlines.name, source: LoggerSource.Game, message: 'Winlines constructor' });
    }

    public show(step: WinlineCycleStep): ICancelablePromise[] {
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        const promises: ICancelablePromise[] = [];
        if (step.phase === ESymbolsHighlighterPhases.NORMAL_CYCLE_PHASE) {
            for (let reel = 0; reel < step.selectionMask.length; reel++) {
                for (let row = 0; row < step.selectionMask[reel].length; row++) {
                    if (!step.selectionMask[reel][row]) continue;
                    const symbol = visibleSymbols[reel][row];
                    if (symbol.isBlocked) continue;
                    promises.push(symbol.symbolAnimation.waitForAnimationEvent(symbol.getAnimationVariant(ESymbolAnimations.WIN, step), 'complete'));
                }
            }
        }
        if (this.generatedWinlines) {
            this.generatedWinlines.destroyWinline();
            const { win } = ConfigLoader.getConfiguration(EConfigurationNames.GAME);
            const isTakeInitiated = this.gameStore.get(EGameStoreKeys.IsTakeInitiated);
            if (!isTakeInitiated) this.generatedWinlines.showWinline(step.payline, step.paylineId);
            const timerPromise: ICancelablePromise = {
                promise: new Promise<void>((resolve) => {
                    this.scene.time.delayedCall(win.generatedWinlineConfig.duration, resolve);
                }),
                cancel: () => {}
            };
            promises.push(timerPromise);
        }
        if (this.winlinesSpine) promises.push(this.winlinesSpine.waitForAnimationEvent(EGameEvents.CYCLE_WINLINE_$, 'complete', [step.paylineId + 1]));
        if (promises.length === 0 && step.phase === ESymbolsHighlighterPhases.NORMAL_CYCLE_PHASE) {
            const timerPromise: ICancelablePromise = {
                promise: new Promise<void>((resolve) => {
                    this.scene.time.delayedCall(Winlines.DEFAULT_CYCLE_TIME, resolve);
                }),
                cancel: () => {}
            };
            promises.push(timerPromise);
        }

        EventsCenter.emit(EGameEvents.CYCLE_WINLINE, step);
        EventsCenter.emit(EGameEvents.CYCLE_WINLINE_$, { eventIds: [step.paylineId + 1] });
        return promises;
    }

    public hide(): void {
        this.reset();
    }

    public getAnimationDuration(cycleIndex: number = 0): number {
        return this.winlineDurations[cycleIndex] || Winlines.DEFAULT_CYCLE_TIME;
    }

    public onCreate(): void {
        const winConfig = this.gameStore.get(EGameStoreKeys.WinConfiguration);

        if (winConfig.useGeneratedWinlines) {
            this.initGeneratedWinlines();
        }

        const spineKey = this.getWinlinesSpineKey();
        if (spineKey) {
            this.initSpineWinlines(spineKey);
        }
    }

    private initGeneratedWinlines(): void {
        const maxPaylines = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).payLines.length;
        const { duration } = this.gameStore.get(EGameStoreKeys.WinConfiguration).generatedWinlineConfig;
        this.generatedWinlines = new GeneratedWinlines(this.scene);
        this.winlineDurations = new Array(maxPaylines).fill(duration);
    }

    private initSpineWinlines(spineKey: string): void {
        const maxPaylines = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).payLines.length;

        this.winlinesSpine = new SpineWrapper(
            this.scene,
            {
                x: this.scene.screen.width / 2,
                y: this.scene.screen.height / 2
            },
            { dataKey: spineKey },
            true
        );
        this.winlinesSpine.spine.zIndex = ELayeringOrder.WINLINES;

        for (let i = 0; i < maxPaylines; i++) {
            const animName = this.winlinesSpine.eventManager.getAnimationNameForGameEvent(EGameEvents.CYCLE_WINLINE_$, [i]);
            this.winlineDurations[i] = animName ? this.winlinesSpine.getAnimationDuration(animName) : 0;
        }
    }

    private getWinlinesSpineKey(): string | undefined {
        return AssetsLoadManager.getSpinesConfiguration().assets.find((spine) => spine.key === 'winlines')?.key;
    }

    private reset(): void {
        if (this.winlinesSpine) {
            this.winlinesSpine.stop(0);
            this.winlinesSpine.spine.skeleton.setupPose();
        }

        if (this.generatedWinlines) this.generatedWinlines.destroyWinline();
    }
}
